import { useMemo } from 'react';
import { useQuery, useInfiniteQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import { SearchServiceQueryKeys } from './config';
import { SearchService } from './service';
import { useSearchStore } from '../../store/useSearchStore';
import {
    CategorizedSearch,
    FilteredSearch,
    SearchCount,
    SearchFilters,
    SearchResponse,
    SearchResult,
} from 'features/search/types';
import { SearchTypes } from 'features/search/enums';
import { convertFiltersForApi } from 'features/search/helpers';
import { ObjectStatus } from 'features/search/filters/enums';
import { getCurrentSiteId } from 'features/services/utils/env-helper';
import { apiErrorFrom } from 'features/errors';

// ─── Main search hooks ───────────────────────────────────────────

/**
 * Query for stages and areas (returns all results, no pagination).
 */
export const useStagesAndAreasQuery = (enabled: boolean = true) => {
    const filters = useSearchStore(state => state.filters);
    const currentSort = useSearchStore(state => state.currentSort);

    const apiFilters = useMemo(() => {
        const f = convertFiltersForApi(filters);
        f.objectStatus = [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.PRESELLING];
        return f;
    }, [filters]);

    return useQuery({
        queryKey: [...SearchServiceQueryKeys.searchResults(), 'stagesAndAreas', apiFilters, currentSort],
        queryFn: async () => {
            const response = await SearchService.fetchStagesAndAreas(apiFilters, currentSort);
            if (response.error) throw apiErrorFrom(response);
            return response.data!;
        },
        enabled,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        select: (data: SearchResponse<CategorizedSearch>) => ({
            stages: data.hits?.stages?.length > 0 ? data.hits.stages : [],
            areas: data.hits?.areas?.length > 0 ? data.hits.areas : [],
            stageFacet: data.facetDistribution?.type?.stage ?? 0,
            areaFacet: data.facetDistribution?.type?.area ?? 0,
        }),
    });
};

/**
 * Infinite query for objects (paginated, append pattern).
 */
export const useObjectsInfiniteQuery = (enabled: boolean = true) => {
    const filters = useSearchStore(state => state.filters);
    const currentSort = useSearchStore(state => state.currentSort);
    const objectLimit = useSearchStore(state => state.objectLimit);

    const apiFilters = useMemo(() => {
        const f = convertFiltersForApi(filters);
        f.objectStatus = [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.PRESELLING];
        return f;
    }, [filters]);

    return useInfiniteQuery({
        queryKey: [...SearchServiceQueryKeys.searchResults(), 'objects', apiFilters, currentSort],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await SearchService.fetchObjects(apiFilters, currentSort, pageParam, objectLimit);
            if (response.error) throw apiErrorFrom(response);
            return response.data!;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const totalFetched = allPages.reduce((sum, page) => sum + (page.hits?.length ?? 0), 0);
            const estimated = lastPage.estimatedTotalHits ?? 0;
            return totalFetched < estimated ? totalFetched : undefined;
        },
        enabled,
        staleTime: 30_000,
    });
};

interface SearchData {
    searchResult: FilteredSearch;
    searchCount: SearchCount;
    isLoading: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
}

/**
 * Composite hook combining stages/areas + paginated objects.
 * Returns search results, counts, loading state, and pagination controls.
 */
export const useSearchData = (enabled: boolean = true): SearchData => {
    const stagesQuery = useStagesAndAreasQuery(enabled);
    const objectsQuery = useObjectsInfiniteQuery(enabled);

    const stages = stagesQuery.data?.stages ?? [];
    const areas = stagesQuery.data?.areas ?? [];
    const objects = useMemo(
        () => objectsQuery.data?.pages.flatMap(page => page.hits ?? []) ?? [],
        [objectsQuery.data],
    );

    const searchResult: FilteredSearch = useMemo(() => ({
        [SearchTypes.STAGE]: stages,
        [SearchTypes.AREA]: areas,
        [SearchTypes.OBJECTS]: objects,
    }), [stages, areas, objects]);

    const searchCount: SearchCount = useMemo(() => ({
        stages: stagesQuery.data?.stageFacet ?? 0,
        areas: stagesQuery.data?.areaFacet ?? 0,
        objects: objectsQuery.data?.pages[0]?.estimatedTotalHits ?? 0,
    }), [stagesQuery.data, objectsQuery.data]);

    const isLoading = stagesQuery.isLoading || objectsQuery.isLoading;

    return {
        searchResult,
        searchCount,
        isLoading,
        fetchNextPage: objectsQuery.fetchNextPage,
        hasNextPage: !!objectsQuery.hasNextPage,
        isFetchingNextPage: objectsQuery.isFetchingNextPage,
    };
};

// ─── Stage object selection (used in SearchResultCardView) ───────

/**
 * Mutation for selecting objects from a specific stage.
 */
export const useSelectObjectsFromStage = () => {
    const filters = useSearchStore(state => state.filters);
    const currentSort = useSearchStore(state => state.currentSort);
    const setObjectResults = useSearchStore(state => state.setObjectResults);

    return useMutation({
        mutationFn: async (stageId: string) => {
            const apiFilters = convertFiltersForApi(filters);
            const filtersWithStage = {
                ...apiFilters,
                stageId: [stageId],
            };

            const response = await SearchService.fetchObjects(filtersWithStage, currentSort, 0, 300);
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        },
        onSuccess: (data) => {
            setObjectResults(data);
        },
    });
};

// ─── Tag search hooks ────────────────────────────────────────────

/**
 * Hook for searching tags by URLs.
 */
export const useSearchTagsByUrls = (urls: string[], enabled: boolean = true) => {
    return useQuery({
        queryKey: SearchServiceQueryKeys.tagsByUrls(urls),
        queryFn: async () => {
            const response = await SearchService.searchTagsByUrls(urls);
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        },
        enabled: enabled && urls.length > 0,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Hook for searching tags by string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic search filter object
export const useSearchTagsByString = (text: string, filter: any, limit: number = 20, enabled: boolean = true) => {
    return useQuery({
        queryKey: SearchServiceQueryKeys.tagsByString(text),
        queryFn: async () => {
            const response = await SearchService.searchTagsByString(text, filter, limit);
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        },
        enabled: enabled && text.length > 0,
        staleTime: 30_000,
    });
};

// ─── Utility hooks ───────────────────────────────────────────────

/**
 * Hook for fetching stage counties.
 */
export const useStageCounties = (siteId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: SearchServiceQueryKeys.stageCounties(siteId),
        queryFn: async () => {
            const response = await SearchService.searchStageCounties(siteId);
            if (response.error) throw apiErrorFrom(response);
            return response.data!;
        },
        enabled: enabled && !!siteId,
        staleTime: 10 * 60 * 1000,
    });
};

/**
 * Hook for fetching parking spaces.
 */
export const useParkingSpaces = (stageIds: string[], enabled: boolean = true) => {
    // Pages without a stage id passed [undefined] here, which still counted as one id.
    const validStageIds = stageIds.filter(Boolean);

    return useQuery({
        queryKey: [...SearchServiceQueryKeys.parkingSpaces(), validStageIds],
        queryFn: async () => {
            const response = await SearchService.fetchParkingSpaces(
                { siteId: [getCurrentSiteId()], stageId: validStageIds, objectStatus: [ObjectStatus.FOR_SALE, ObjectStatus.BOOKED, ObjectStatus.SOLD] },
                '',
                0,
                200
            );
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        },
        enabled: enabled && validStageIds.length > 0,
        staleTime: 30_000,
    });
};
